import React, { useState, useEffect } from 'react';
import { Modal, Button, Radio, message } from 'antd';
import { X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { uploadFileApi, editConsultationObservationApi } from '../../redux/api/Call/ConsultationObservationSlice';
import { endCallApi } from '../../redux/api/Call/EndCallSlice';
import NotesAndRecommendations from './index';
import './styles.css';

const RISK_OPTIONS = [
  { label: "High Risk", value: "HIGH_RISK", color: "red" },
  { label: "Moderate Risk", value: "MODERATE_RISK", color: "orange" },
  { label: "Low Risk", value: "LOW_RISK", color: "goldenrod" },
  { label: "No Risk", value: "NO_RISK", color: "green" },
];

const EditSessionModal = ({ visible, onClose, sessionData, onSessionUpdated }) => {
  const dispatch = useDispatch();
  const { 
    editLoading, 
    uploadLoading,
    editError 
  } = useSelector((state) => state.CONSULTATION_OBSERVATION);

  // Modal state for all consultation observation fields
  const [notes, setNotes] = useState('');
  const [prescription, setPrescription] = useState('');
  const [prescriptionNotRequired, setPrescriptionNotRequired] = useState(false);
  const [recommendationFiles, setRecommendationFiles] = useState([]);

  // Observation states
  const [selfHarmSymptoms, setSelfHarmSymptoms] = useState("LOW");
  const [depressionSymptoms, setDepressionSymptoms] = useState("LOW");
  const [anxietySymptoms, setAnxietySymptoms] = useState("LOW");
  const [angerIssues, setAngerIssues] = useState("LOW");

  // Recommendation states  
  const [assessments, setAssessments] = useState("");
  const [exercises, setExercises] = useState("");
  const [clinicalConsultation, setClinicalConsultation] = useState(false);
  const [medicalConsultation, setMedicalConsultation] = useState(false);

  // File upload states
  const [assessmentFiles, setAssessmentFiles] = useState([]);
  const [exerciseFiles, setExerciseFiles] = useState([]);

  // Risk is only asked for when the end-of-call form was never saved (status RISK MISSING)
  const riskMissing = sessionData?.currentStatus === "RISK MISSING";
  const [riskStatus, setRiskStatus] = useState(null);

  // Mobile responsiveness state
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize form data when sessionData changes
  useEffect(() => {
    if (sessionData) {
      setRiskStatus(sessionData.riskStatus || null);
      // Basic fields
      setNotes(sessionData.notes || '');
      setPrescription(sessionData.prescription || '');
      setPrescriptionNotRequired(sessionData.prescriptionNotRequired || false);
      setRecommendationFiles(sessionData.recommendationFiles || []);

      // Observation fields - use real API data
      setSelfHarmSymptoms(sessionData.selfHarmSymptoms || "LOW");
      setDepressionSymptoms(sessionData.depressionSymptoms || "LOW");
      setAnxietySymptoms(sessionData.anxietySymptoms || "LOW");
      setAngerIssues(sessionData.angerIssues || "LOW");

      // Consultation flags
      setClinicalConsultation(sessionData.clinicalConsultation || false);
      setMedicalConsultation(sessionData.medicalConsultation || false);

      // Extract assessment and exercise info from prescriptions array
      const prescriptions = sessionData.prescriptions || [];
      const assessmentPrescription = prescriptions.find(p => p.prescriptionType === "ASSESSMENT");
      const exercisePrescription = prescriptions.find(p => p.prescriptionType === "EXERCISE");
      
      setAssessments(assessmentPrescription?.fileName || "");
      setExercises(exercisePrescription?.fileName || "");

      // Reset file states as we're editing existing data
      setAssessmentFiles([]);
      setExerciseFiles([]);
    }
  }, [sessionData]);

  // Function to upload files and get URLs (similar to call/index.js)
  const uploadFiles = async () => {
    const uploadPromises = [];
    const fileUrls = {};

    // Upload assessment files
    if (assessmentFiles.length > 0) {
      assessmentFiles.forEach((file) => {
        uploadPromises.push(
          dispatch(uploadFileApi(file)).then((result) => {
            if (result.payload.status === "success") {
              fileUrls.assessmentsFileURL = result.payload.url;
              // Use the assessment input field value as filename, fallback to actual filename if empty
              fileUrls.assessmentsFileName = assessments || file.name;
            }
          })
        );
      });
    }

    // Upload exercise files
    if (exerciseFiles.length > 0) {
      exerciseFiles.forEach((file) => {
        uploadPromises.push(
          dispatch(uploadFileApi(file)).then((result) => {
            if (result.payload.status === "success") {
              fileUrls.exercisesFileURL = result.payload.url;
              // Use the exercises input field value as filename, fallback to actual filename if empty
              fileUrls.exercisesFileName = exercises || file.name;
            }
          })
        );
      });
    }

    // Upload recommendation files
    if (recommendationFiles.length > 0) {
      recommendationFiles.forEach((file) => {
        uploadPromises.push(
          dispatch(uploadFileApi(file)).then((result) => {
            if (result.payload.status === "success") {
              fileUrls.recommendationsFileURL = result.payload.url;
              fileUrls.recommendationsFileName = file.name;
            }
          })
        );
      });
    }

    await Promise.all(uploadPromises);
    return fileUrls;
  };

  const handleUpdate = async () => {
    if (riskMissing && !riskStatus) {
      message.error("Please rate the member's risk status before saving.");
      return;
    }
    try {
      // Step 1: Upload any new files first
      const fileUrls = await uploadFiles();

      // Step 2: Prepare consultation observation data for edit
      const observationData = {
        appointmentId: sessionData.appointmentId,
        selfHarmSymptoms: selfHarmSymptoms,
        depressionSymptoms: depressionSymptoms,
        anxietySymptoms: anxietySymptoms,
        angerIssues: angerIssues,
        clinicalConsultation: clinicalConsultation,
        medicalConsultation: medicalConsultation,
        notesText: notes || "",
        recommendationText: prescription || "",
        noPrescriptionRequired: prescriptionNotRequired,
        ...fileUrls // Spread any newly uploaded file URLs
      };

      // If no new files were uploaded, preserve existing file URLs
      if (!fileUrls.assessmentsFileURL && sessionData.prescriptions) {
        const assessmentPrescription = sessionData.prescriptions.find(p => p.prescriptionType === "ASSESSMENT");
        if (assessmentPrescription) {
          observationData.assessmentsFileURL = assessmentPrescription.url;
          observationData.assessmentsFileName = assessments || assessmentPrescription.fileName;
        }
      }

      if (!fileUrls.exercisesFileURL && sessionData.prescriptions) {
        const exercisePrescription = sessionData.prescriptions.find(p => p.prescriptionType === "EXERCISE");
        if (exercisePrescription) {
          observationData.exercisesFileURL = exercisePrescription.url;
          observationData.exercisesFileName = exercises || exercisePrescription.fileName;
        }
      }

      // Step 3: Send edit consultation observation data
      const result = await dispatch(editConsultationObservationApi(observationData));

      console.log("Edit result:", result); // Debug log
      
      if (result.payload && result.payload.status === "success") {
        // Step 4: Update appointment status based on recommendations
        const hasRecommendationText = prescription && prescription.trim() !== "";
        const hasRecommendationFiles = recommendationFiles && recommendationFiles.length > 0;
        const hasRecommendations = hasRecommendationText || hasRecommendationFiles;

        let appointmentStatus;
        if (prescriptionNotRequired) {
          appointmentStatus = "COMPLETED";
        } else {
          appointmentStatus = hasRecommendations ? "COMPLETED" : "IN_COMPLETE";
        }

        // Update appointment status
        const statusUpdateData = {
          id: sessionData.appointmentId,
          status: appointmentStatus,
          ...(riskMissing ? { riskStatus } : {}),
        };

        await dispatch(endCallApi(statusUpdateData));

        message.success("Session details updated successfully!");
        
        // Call callback to refresh session data
        if (onSessionUpdated) {
          onSessionUpdated();
        }
        
        // Close modal after successful update
        onClose();
      } else {
        // Handle error cases
        console.log("Error result payload:", result.payload); // Debug log
        
        let errorFound = false;
        
        // Check for the specific maximum limit error in various response structures
        if (result.payload && result.payload.status === "error") {
          // Check fullError first (most reliable)
          if (result.payload.fullError) {
            const fullError = result.payload.fullError;
            
            // Check payload string
            if (fullError.payload && typeof fullError.payload === 'string' && 
                fullError.payload.includes("Maximum limit of 2 operations")) {
              message.error("This session cannot be updated further. Maximum edit limit has been reached.");
              errorFound = true;
            }
            // Check errorResponse.errorMessage
            else if (fullError.errorResponse && fullError.errorResponse.errorMessage) {
              message.error(fullError.errorResponse.errorMessage);
              errorFound = true;
            }
          }
          
          // Fallback: check the message field
          if (!errorFound && result.payload.message) {
            const errorMessage = result.payload.message;
            
            // Check if message is object with payload
            if (typeof errorMessage === 'object' && errorMessage.payload && 
                typeof errorMessage.payload === 'string' && 
                errorMessage.payload.includes("Maximum limit of 2 operations")) {
              message.error("This session cannot be updated further. Maximum edit limit has been reached.");
              errorFound = true;
            }
            // Check if message is string
            else if (typeof errorMessage === 'string' && errorMessage.includes("Maximum limit of 2 operations")) {
              message.error("This session cannot be updated further. Maximum edit limit has been reached.");
              errorFound = true;
            }
          }
        }
        
        // Generic error fallback
        if (!errorFound) {
          message.error("Failed to update session details. Please try again.");
        }
      }
    } catch (error) {
      console.error("Update error:", error);
      
      // Handle error response from API
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.payload && typeof errorData.payload === 'string' && 
            errorData.payload.includes("Maximum limit of 2 operations")) {
          message.error("This session cannot be updated further. Maximum edit limit has been reached.");
        } else if (errorData.errorResponse && errorData.errorResponse.errorMessage) {
          message.error(errorData.errorResponse.errorMessage);
        } else {
          message.error("An error occurred while updating session details.");
        }
      } else {
        message.error("An error occurred while updating session details.");
      }
    }
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isMobile ? '100vw' : 550}
      className="edit-session-modal"
      closeIcon={false}
      styles={{
        body: { padding: 0 },
        header: { display: 'none' }
      }}
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        height: '100vh',
        margin: 0,
        maxWidth: 'none'
      }}
      wrapClassName="edit-session-modal-wrap"
    >
      <div className="modal-content">
        {/* Modal Header */}
        <div className="modal-header">
          <button onClick={onClose} className="modal-close-btn">
            <X className={`${isMobile ? 'h-5 w-5' : 'h-6 w-6'} text-[#171717]`} />
          </button>
          <h2 className="modal-title">Edit Session Details</h2>
        </div>

        {/* Note about editing */}
        <div className="modal-note">
          <p className="modal-note-text">
            {sessionData?.editable === false ? (
              <>
                <strong>Note:</strong> This session has already been edited and cannot be modified again.
              </>
            ) : (
              <>
                <strong>Note:</strong> These details can only be edited once. Please review carefully before saving.
              </>
            )}
          </p>
        </div>

        {/* Modal Body - Scrollable Content */}
        <div className="modal-body">
          {riskMissing && (
            <div className="mb-4 rounded-xl p-3" style={{ background: "#FFF4E5", border: "1px solid #FFAF77" }}>
              <p className="font-poppins font-semibold text-[#171717] mb-2">
                Risk status (required) - this session was never saved after the call
              </p>
              <Radio.Group value={riskStatus} onChange={(e) => setRiskStatus(e.target.value)}>
                {RISK_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value} style={{ color: option.color }}>
                    {option.label}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
          )}
          <NotesAndRecommendations
            notes={notes}
            setNotes={setNotes}
            prescription={prescription}
            setPrescription={setPrescription}
            prescriptionNotRequired={prescriptionNotRequired}
            setPrescriptionNotRequired={setPrescriptionNotRequired}
            recommendationFiles={recommendationFiles}
            setRecommendationFiles={setRecommendationFiles}
            // New observation props
            selfHarmSymptoms={selfHarmSymptoms}
            setSelfHarmSymptoms={setSelfHarmSymptoms}
            depressionSymptoms={depressionSymptoms}
            setDepressionSymptoms={setDepressionSymptoms}
            anxietySymptoms={anxietySymptoms}
            setAnxietySymptoms={setAnxietySymptoms}
            angerIssues={angerIssues}
            setAngerIssues={setAngerIssues}
            // New recommendation props
            assessments={assessments}
            setAssessments={setAssessments}
            exercises={exercises}
            setExercises={setExercises}
            clinicalConsultation={clinicalConsultation}
            setClinicalConsultation={setClinicalConsultation}
            medicalConsultation={medicalConsultation}
            setMedicalConsultation={setMedicalConsultation}
            // File upload props
            assessmentFiles={assessmentFiles}
            setAssessmentFiles={setAssessmentFiles}
            exerciseFiles={exerciseFiles}
            setExerciseFiles={setExerciseFiles}
            inCall={false}
          />
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <Button 
            type="primary" 
            onClick={handleUpdate} 
            className="modal-update-btn"
            loading={editLoading || uploadLoading}
            disabled={sessionData?.editable === false} // Disable if already edited once
          >
            {editLoading || uploadLoading ? "Updating..." : "Update"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditSessionModal;